import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CSpinner,
  CAlert,
  CTable,
  CBadge,
  CTableBody,
  CTableRow,
  CTableDataCell,
  CTableHeaderCell
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilCheckCircle, cilArrowLeft, cilDescription, cilReload, cilCalendar } from '@coreui/icons';
import quickbooksService from '../services/quickbooksService';

const ThankYouPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRedirect, setAutoRedirect] = useState(false);

  // ✅ Check if this is a renewal or new purchase
  const isRenewal = location.state?.isRenewal || false;
  const subscriptionNumber = location.state?.subscriptionNumber || '';
  const originalSubscriptionNumber = location.state?.originalSubscriptionNumber || '';

  useEffect(() => {
    const verifyPaymentAndRedirect = async () => {
      try {
        const invoiceId = location.state?.invoiceId;
        
        if (!invoiceId) {
          setError('No payment information found. Please check your email for confirmation.');
          setLoading(false);
          return;
        }

        console.log("🔄 Verifying payment for invoice:", invoiceId);

        // Use the enhanced payment check endpoint
        const response = await quickbooksService.checkPaymentStatus(invoiceId);
        
        if (response.success) {
          setPaymentData(response);
          
          if (response.payment_status === 'paid') {
            console.log("✅ Payment verified as paid - SHOWING THANK YOU PAGE");
            setAutoRedirect(true);
            // ✅ REMOVED the automatic redirect to subscriptions
            // User stays on thank you page until they manually click
          } else {
            // Enhanced polling with exponential backoff
            startEnhancedPolling(invoiceId);
          }
        } else {
          setError('Unable to verify payment status. Please check your email for confirmation.');
        }
      } catch (err) {
        console.error('Error verifying payment:', err);
        setError('Failed to verify payment status. Please check your email for confirmation.');
      } finally {
        setLoading(false);
      }
    };

    const startEnhancedPolling = (invoiceId) => {
      let attempts = 0;
      const maxAttempts = 36; // 15 minutes total (36 * 25 seconds)
      
      const poll = async () => {
        try {
          attempts++;
          console.log(`🔄 Polling attempt ${attempts} for invoice: ${invoiceId}`);
          
          const response = await quickbooksService.checkPaymentStatus(invoiceId);
          
          if (response.success && response.payment_status === 'paid') {
            console.log("✅ Payment completed during polling - SHOWING THANK YOU PAGE");
            setPaymentData(response);
            setAutoRedirect(true);
            // ✅ REMOVED the automatic redirect to subscriptions
            return;
          }
          
          // Continue polling if not paid and within max attempts
          if (attempts < maxAttempts) {
            // Exponential backoff: 5s, 10s, 15s, 20s, then 25s
            const delay = Math.min(25000, 5000 + (attempts * 5000));
            setTimeout(poll, delay);
          } else {
            console.log("⏰ Payment polling stopped after maximum attempts");
            setError('Payment verification timeout. Please check your email for confirmation.');
          }
        } catch (error) {
          console.error('Polling error:', error);
          
          // Continue polling on error (with delay)
          if (attempts < maxAttempts) {
            setTimeout(poll, 10000);
          }
        }
      };

      // Start polling
      poll();
    };

    verifyPaymentAndRedirect();
  }, [location, navigate, isRenewal]);

  const formatPrice = (price) => {
    if (!price && price !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const handleManualCheck = async () => {
    if (!paymentData) return;
    
    setLoading(true);
    try {
      const response = await quickbooksService.checkPaymentStatus(location.state?.invoiceId);
      if (response.success) {
        setPaymentData(response);
        if (response.payment_status === 'paid') {
          setAutoRedirect(true);
        }
      }
    } catch (err) {
      console.error('Manual check error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSubscriptions = () => {
    navigate('/subscriptions', { 
      state: { 
        paymentSuccess: true,
        invoiceNumber: paymentData?.invoice_number 
      }
    });
  };

  const handleBackToClients = () => {
    navigate('/clients');
  };

  if (loading) {
    return (
      <CContainer className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <CSpinner color="primary" /> {/* ✅ REMOVED size="lg" */}
          <p className="mt-3">Verifying your {isRenewal ? 'renewal' : 'payment'}...</p>
          <small className="text-muted">This may take a few moments</small>
        </div>
      </CContainer>
    );
  }

  if (error) {
    return (
      <CContainer className="mt-5">
        <CAlert color="warning">
          <h4>{isRenewal ? 'Renewal Verification' : 'Payment Verification'}</h4>
          <p>{error}</p>
          <div className="d-flex gap-2">
            <CButton color="primary" onClick={() => navigate('/subscriptions')}>
              Back to Subscriptions
            </CButton>
            <CButton color="outline-primary" onClick={() => navigate('/clients')}>
              Back to Clients
            </CButton>
          </div>
        </CAlert>
      </CContainer>
    );
  }

  return (
    <CContainer className="mt-5">
      <CRow className="justify-content-center">
        <CCol md={8} lg={6}>
          <CCard className="text-center">
            <CCardHeader className={autoRedirect ? "bg-success text-white" : "bg-warning text-white"}>
              <CIcon icon={autoRedirect ? cilCheckCircle : cilReload} size="xl" className="mb-2" />
              <h3 className="mb-0">
                {autoRedirect 
                  ? (isRenewal ? 'Renewal Successful!' : 'Payment Successful!')
                  : (isRenewal ? 'Renewal Processing' : 'Payment Processing')
                }
              </h3>
            </CCardHeader>
            <CCardBody className="p-4">
              <div className="mb-4">
                <h5 className={autoRedirect ? "text-success" : "text-warning"}>
                  {autoRedirect 
                    ? (isRenewal ? 'Thank You for Renewing!' : 'Thank You for Your Payment!')
                    : (isRenewal ? 'Renewal Being Processed' : 'Payment Being Processed')
                  }
                </h5>
                <p className="text-muted">
                  {autoRedirect 
                    ? (isRenewal 
                        ? 'Your subscription has been renewed successfully. Your license will be extended immediately.'
                        : 'Your payment has been processed successfully. Your order is now being processed.'
                      )
                    : (isRenewal
                        ? 'Your renewal is being processed. This page will update automatically when completed.'
                        : 'Your payment is being processed. This page will update automatically when completed.'
                      )
                  }
                </p>
              </div>

              {paymentData && (
                <CCard className="mb-4">
                  <CCardBody>
                    <h6>{isRenewal ? 'Renewal Details' : 'Payment Details'}</h6>
                    <CTable striped bordered responsive>
                      <CTableBody>
                        <CTableRow>
                          <CTableHeaderCell>Invoice Number:</CTableHeaderCell>
                          <CTableDataCell>{paymentData.invoice_number}</CTableDataCell>
                        </CTableRow>
                        {isRenewal && subscriptionNumber && (
                          <CTableRow>
                            <CTableHeaderCell>Renewal Subscription:</CTableHeaderCell>
                            <CTableDataCell>
                              <CBadge color="info">{subscriptionNumber}</CBadge>
                              {originalSubscriptionNumber && (
                                <small className="text-muted d-block">
                                  Original: {originalSubscriptionNumber}
                                </small>
                              )}
                            </CTableDataCell>
                          </CTableRow>
                        )}
                        <CTableRow>
                          <CTableHeaderCell>Customer Name:</CTableHeaderCell>
                          <CTableDataCell>{paymentData.customer_name || 'Customer'}</CTableDataCell>
                        </CTableRow>
                        <CTableRow>
                          <CTableHeaderCell>Status:</CTableHeaderCell>
                          <CTableDataCell>
                            <CBadge color={autoRedirect ? "success" : "warning"}>
                              {autoRedirect ? (isRenewal ? "Renewed" : "Paid") : "Processing"}
                            </CBadge>
                          </CTableDataCell>
                        </CTableRow>
                        {paymentData.amounts && (
                          <>
                            <CTableRow>
                              <CTableHeaderCell>Total Amount:</CTableHeaderCell>
                              <CTableDataCell>{formatPrice(paymentData.amounts.total_due)}</CTableDataCell>
                            </CTableRow>
                            <CTableRow>
                              <CTableHeaderCell>Tax:</CTableHeaderCell>
                              <CTableDataCell>{formatPrice(paymentData.amounts.tax_amount)}</CTableDataCell>
                            </CTableRow>
                            <CTableRow>
                              <CTableHeaderCell>Discount:</CTableHeaderCell>
                              <CTableDataCell className="text-danger">
                                -{formatPrice(paymentData.amounts.discount_amount)}
                              </CTableDataCell>
                            </CTableRow>
                          </>
                        )}
                      </CTableBody>
                    </CTable>
                  </CCardBody>
                </CCard>
              )}

              <div className="d-grid gap-2 d-md-flex justify-content-md-center">
                {autoRedirect ? (
                  <>
                    <CButton color="primary" onClick={handleViewSubscriptions}>
                      <CIcon icon={isRenewal ? cilCalendar : cilDescription} className="me-2" />
                      {isRenewal ? 'View Subscriptions' : 'View All Subscriptions'}
                    </CButton>
                    <CButton color="outline-primary" onClick={handleBackToClients}>
                      <CIcon icon={cilArrowLeft} className="me-2" />
                      Back to Clients
                    </CButton>
                  </>
                ) : (
                  <>
                    <CButton color="warning" onClick={handleManualCheck}>
                      <CIcon icon={cilReload} className="me-2" />
                      Check {isRenewal ? 'Renewal' : 'Payment'} Status
                    </CButton>
                    <CButton color="outline-primary" onClick={() => navigate('/subscriptions')}>
                      Return to Subscriptions
                    </CButton>
                  </>
                )}
              </div>

              <div className="mt-4 p-3 bg-light rounded">
                <h6>What happens next?</h6>
                {autoRedirect ? (
                  <>
                    {isRenewal ? (
                      <>
                        <p className="mb-1 small">• Your subscription has been extended for 1 year</p>
                        <p className="mb-1 small">• You will receive a renewal confirmation email</p>
                        <p className="mb-1 small">• License activation is immediate - no downtime</p>
                        <p className="mb-0 small">• Contact support if you have any questions</p>
                      </>
                    ) : (
                      <>
                        <p className="mb-1 small">• You will receive a confirmation email with your invoice</p>
                        <p className="mb-1 small">• Your software license will be activated within 24 hours</p>
                        <p className="mb-0 small">• Contact support if you have any questions</p>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <p className="mb-1 small">• {isRenewal ? 'Renewal' : 'Payment'} processing typically takes 1-2 minutes</p>
                    <p className="mb-1 small">• This page will update automatically when {isRenewal ? 'renewal' : 'payment'} is complete</p>
                    <p className="mb-0 small">• You will receive an email confirmation once processed</p>
                  </>
                )}
              </div>

              {/* ✅ Special Renewal Success Message */}
              {autoRedirect && isRenewal && (
                <CAlert color="info" className="mt-3">
                  <CIcon icon={cilCalendar} className="me-2" />
                  <strong>Renewal Complete!</strong> Your subscription has been successfully extended. 
                  All features remain active without interruption.
                </CAlert>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  );
};

export default ThankYouPage;